import type { MouseEvent } from 'react';

import { faMicrophone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import type { VoiceButtonProps } from './survey-ai-forms-voice.types';

/**
 * Voice button presentational component
 * Displays microphone icon with active/inactive states
 *
 * States:
 * - Default: Gray background, black text
 * - Active/Listening: White background with subtle border, black text
 * - Disabled: Reduced opacity, cursor not-allowed
 *
 * @example
 * ```tsx
 * <SurveyAiFormsVoiceButton
 *   isListening={true}
 *   isSupported={true}
 *   onToggle={() => toggleVoice()}
 *   hideLabel={true}
 * />
 * ```
 */
export default function SurveyAiFormsVoiceButton({
  isListening,
  isSupported,
  error,
  onToggle,
  className = '',
  hideLabel = false,
  loading = false,
  isFormSection = false,
  responseMode,
  disabled = false,
}: VoiceButtonProps) {
  const t = useTranslations('AIForms.voice');

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  const getTitle = () => {
    if (!isSupported) return t('unsupportedTitle');
    return error || undefined;
  };

  // Show active immediately (responseMode) instead of waiting for mic setup (isListening)
  const isActive = responseMode === 'voice' || isListening;

  return (
    <button
      type='button'
      data-active={isActive}
      data-icon-only={hideLabel}
      onClick={handleClick}
      disabled={!isSupported || loading || disabled}
      title={getTitle()}
      data-test='voice-button'
      className={cn(
        'relative flex cursor-pointer items-center justify-center gap-2 rounded-full px-3 py-2 [font-family:var(--brand-font)] text-xs/4 font-medium transition-all disabled:cursor-not-allowed data-[active=true]:border-2 data-[active=true]:border-black/8 data-[icon-only=true]:data-[active=true]:border-gray-300',
        isFormSection
          ? 'text-black data-[active=false]:bg-gray-100 data-[icon-only=true]:data-[active=false]:bg-gray-100'
          : 'data-[active=false]:bg-(--brand-bg-unselected) data-[active=false]:text-(--brand-text-unselected) data-[active=true]:text-black data-[icon-only=true]:data-[active=false]:bg-(--brand-bg-unselected)',
        'data-[active=false]:hover:bg-gray-200 data-[active=true]:bg-(--brand-assistant-message-bg) data-[icon-only=true]:data-[active=true]:bg-(--brand-assistant-message-bg)',
        className
      )}
    >
      <FontAwesomeIcon icon={faMicrophone} className='size-3' />
      {!hideLabel && <span>{t('label')}</span>}
    </button>
  );
}
